import { useState, useEffect, useMemo, useCallback, useRef } from 'react';

/**
 * Hook to track unsaved changes in a form
 * @param currentValues - Current form values object
 * @param enabled - Whether tracking is enabled (typically modal open state)
 * @returns hasUnsavedChanges boolean and resetInitialValues function
 */
export function useUnsavedChanges<T extends Record<string, unknown>>(
  currentValues: T,
  enabled: boolean = true
) {
  const [initialValues, setInitialValues] = useState<string | null>(null);
  const hasInitialized = useRef(false);
  // Store latest values in ref so resetInitialValues can be stable
  const latestValuesRef = useRef<T>(currentValues);

  // Keep ref in sync with current values
  useEffect(() => {
    latestValuesRef.current = currentValues;
  }, [currentValues]);

  // Reset initialization flag when modal closes
  useEffect(() => {
    if (!enabled) {
      setInitialValues(null);
      hasInitialized.current = false;
    }
  }, [enabled]);

  const hasUnsavedChanges = useMemo(() => {
    if (!enabled || initialValues === null) return false;
    return JSON.stringify(currentValues) !== initialValues;
  }, [currentValues, initialValues, enabled]);

  // STABLE callback - no dependencies on currentValues
  const resetInitialValues = useCallback(() => {
    setInitialValues(JSON.stringify(latestValuesRef.current));
    hasInitialized.current = true;
  }, []);

  return {
    hasUnsavedChanges,
    resetInitialValues
  };
}
